import _ from 'lodash-es';
import angular from 'angular';
import $allSettled from '@/portainer/services/allSettled';
import { getCustomResourceApplications } from '@/react/kubernetes/applications/queries/useApplications';

const colors = ['red', 'orange', 'lime', 'green', 'darkgreen', 'cyan', 'turquoise', 'teal', 'deepskyblue', 'blue', 'darkblue', 'slateblue', 'magenta', 'darkviolet'];

class KubernetesStackLogsController {
  /* @ngInject */
  constructor(Notifications, KubernetesApplicationService, KubernetesPodService, EndpointProvider) {
    this.Notifications = Notifications;
    this.KubernetesApplicationService = KubernetesApplicationService;
    this.KubernetesPodService = KubernetesPodService;
    this.EndpointProvider = EndpointProvider;

    this.generateLogsPromise = this.generateLogsPromise.bind(this);
    this.generateAppPromise = this.generateAppPromise.bind(this);
    this.getStackLogsAsync = this.getStackLogsAsync.bind(this);
  }

  async generateLogsPromise(pod, container, params) {
    const res = {
      Pod: pod,
      Logs: [],
    };
    res.Logs = await this.KubernetesPodService.logs(pod.Namespace, pod.Name, container.Name, params);
    return res;
  }

  // Custom resource applications have a different structure, so we need to handle them differently
  async generateCustomResourceLogsPromise(pod, namespace, params) {
    const res = {
      Pod: pod,
      Logs: [],
    };
    res.Logs = await this.KubernetesPodService.logs(namespace, pod.Name, pod.ContainerName, params);
    return res;
  }

  generateAppPromise(params) {
    return async (app) => {
      const res = {
        Application: app,
        Pods: [],
      };

      const promises = _.flatMap(_.map(app.Pods, (pod) => _.map(pod.Containers, (container) => this.generateLogsPromise(pod, container, params))));
      const result = await $allSettled(promises);
      res.Pods = result.fulfilled;
      return res;
    };
  }

  generateCustomResourceAppPromise(params) {
    return async (app) => {
      const res = {
        Application: app,
        Pods: [],
      };
      const promises = _.flatMap(_.map(app.Pods, (pod) => this.generateCustomResourceLogsPromise(pod, app.ResourcePool, params)));
      const result = await $allSettled(promises);
      res.Pods = result.fulfilled;
      return res;
    };
  }

  async getApplicationStackDataAsync(params) {
    const applications = await this.KubernetesApplicationService.get(this.$transition$.params().namespace);
    const filteredApplications = _.filter(applications, (app) => app.StackName === this.$transition$.params().name);
    const logsPromises = _.map(filteredApplications, this.generateAppPromise(params));
    const data = await Promise.all(logsPromises);
    return data;
  }

  async getCustomResourceStackDataAsync(params) {
    const customResourceApplications = await getCustomResourceApplications(this.EndpointProvider.endpointID(), {
      namespace: this.$transition$.params().namespace,
    });
    const filteredApplications = _.filter(customResourceApplications, (app) => app.StackName === this.$transition$.params().name);
    const logsPromises = _.map(filteredApplications, this.generateCustomResourceAppPromise(params));
    const data = await Promise.all(logsPromises);
    return data;
  }

  async getStackLogsAsync(params) {
    let colorIndex = -1;
    try {
      // Fetch and merge the data from regular and custom resource applications
      const [applicationData, customResourceData] = await Promise.all([this.getApplicationStackDataAsync(params), this.getCustomResourceStackDataAsync(params)]);
      const data = [...applicationData, ...customResourceData];

      const logs = _.flatMap(data, (app) => {
        return _.flatMap(app.Pods, (pod) => {
          colorIndex += 1;
          return {
            sectionName: pod.Pod.Name,
            sectionNameColor: colors[colorIndex % colors.length],
            logs: pod.Logs.logs,
          };
        });
      });

      return logs;
    } catch (err) {
      this.Notifications.error('Failure', err, 'Unable to retrieve application logs');
    }
  }
}

export default KubernetesStackLogsController;
angular.module('portainer.kubernetes').controller('KubernetesStackLogsController', KubernetesStackLogsController);
